// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.

export type ApiErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'VALIDATION_ERROR'
  | 'RATE_LIMITED'
  | 'SERVER_ERROR'
  | 'UNKNOWN';

const RETRYABLE_CODES: ApiErrorCode[] = [
  'NETWORK_ERROR',
  'TIMEOUT',
  'SERVER_ERROR',
  'RATE_LIMITED',
];

export class ApiError extends Error {
  readonly code: ApiErrorCode;
  /**
   * The backend's semantic error code (the `error` field of the JSON body,
   * e.g. `invoice_package_mismatch`). Preserved verbatim so call sites can
   * map it to a localized message. `code` above is only the HTTP-status
   * bucket (`VALIDATION_ERROR` for any 422) and cannot disambiguate.
   */
  readonly serverCode?: string;
  readonly status: number | null;
  readonly details?: Record<string, unknown>;
  readonly retryable: boolean;

  constructor(opts: {
    code: ApiErrorCode;
    message: string;
    status?: number | null;
    details?: Record<string, unknown>;
    serverCode?: string;
  }) {
    super(opts.message);
    this.name = 'ApiError';
    this.code = opts.code;
    this.serverCode = opts.serverCode;
    this.status = opts.status ?? null;
    this.details = opts.details;
    this.retryable = RETRYABLE_CODES.includes(opts.code);
  }
}

export function statusToErrorCode(status: number): ApiErrorCode {
  if (status === 401) return 'UNAUTHORIZED';
  if (status === 403) return 'FORBIDDEN';
  if (status === 404) return 'NOT_FOUND';
  if (status === 422) return 'VALIDATION_ERROR';
  if (status === 429) return 'RATE_LIMITED';
  if (status >= 500) return 'SERVER_ERROR';
  return 'UNKNOWN';
}

/**
 * Sprint 63.42.0 (owner ruling OR-26) — the server refused because the
 * feature's module is not in the installation's package (`403 PACKAGE_LIMIT`
 * from `requireModuleEntitlement`). A page that reaches into another module's
 * API — a core page reading an overlay register, say — treats this as "not in
 * your package, nothing to show", never as a failure to display.
 */
export function isPackageLimitError(err: unknown): boolean {
  return err instanceof ApiError && err.status === 403 && err.serverCode === 'PACKAGE_LIMIT';
}
